import { FormEvent, forwardRef, useEffect, useState } from 'react';
import Input from '../Input';
import { Props as InputProps } from '../Input/Input';
import CrossSVG from '@/svg/search-cross.svg?url';
import Button from '../Button';
import { m, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { useFiltersState } from '@/atoms/catalog/filters-state';
import { produce } from 'immer';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import { SMALL_SEARCH_POPUP_NAME } from '@/components/layout/CatalogFilters/SmallSearchPopup';
import { useIsMounted, useMediaQuery } from 'usehooks-ts';
import classNames from 'classnames';

const MotionButton = m(Button);

const SearchInput = forwardRef<
    HTMLInputElement,
    InputProps & {
        onSubmit?: () => void;
        isBigSubmitBtn?: boolean;
        label?: string;
        onChanged?: (value: string) => void;
    }
>(
    (
        {
            value,
            defaultValue,
            showLabel,
            onSubmit,
            isBigSubmitBtn = false,
            label = 'Введите бренд, артикул, модель ...',
            onChanged,
            ...props
        },
        ref,
    ) => {
        const { closePopup } = useSetOpenedPopupsState();
        const [currentValue, setCurrentValue] = useState<string>((defaultValue as string) || '');
        const router = useRouter();
        const [, setFiltersState] = useFiltersState();

        useEffect(() => {
            if (value && typeof value === 'string') {
                setCurrentValue(value);
            }
        }, [value]);

        return (
            <form
                onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    const search = currentValue.trim();
                    if (!search) return;
                    onSubmit?.();
                    const { sort, order } = router.query;

                    const prevSearches = JSON.parse(localStorage.getItem('searches') || '[]');
                    const newSearches = [search, ...prevSearches].slice(0, 50);
                    localStorage.setItem('searches', JSON.stringify(newSearches));

                    router.push(
                        {
                            pathname: '/search',
                            query: {
                                sort: sort ?? 'published_at',
                                order: order ?? 'desc',
                                search,
                            },
                        },
                        undefined,
                        { shallow: true },
                    );

                    setFiltersState(
                        produce((draft) => {
                            draft.search = search;
                        }),
                    );

                    setTimeout(() => {
                        closePopup(SMALL_SEARCH_POPUP_NAME);
                    }, 500);
                }}
                className="search-input-form"
            >
                <label className="search-input-label">
                    <Input
                        {...props}
                        label={label}
                        inputClass="search-input js-search-input-el"
                        tag="span"
                        showLabel={showLabel}
                        value={currentValue}
                        onChange={(event: FormEvent) => {
                            const eventTarget = event.target as HTMLInputElement;
                            if (eventTarget) {
                                if (typeof eventTarget.value === 'string') {
                                    setCurrentValue(eventTarget.value);
                                    onChanged?.(eventTarget.value);
                                }
                            }
                        }}
                        className={props.className}
                        ref={ref}
                    />
                </label>
                <div className="search-input-buttons">
                    <AnimatePresence mode="wait">
                        {currentValue.length > 0 && (
                            <MotionButton
                                type="button"
                                className="search-input-clear"
                                aria-label="Очистить строку поиска"
                                icon={
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        src={CrossSVG}
                                        alt=""
                                        width={17}
                                        height={17}
                                        className="img-fluid"
                                        decoding="async"
                                    />
                                }
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => {
                                    setCurrentValue('');
                                }}
                            />
                        )}
                    </AnimatePresence>
                    <Button
                        type="submit"
                        className="search-input-submit"
                        geometryVariant="default"
                        disabled={!currentValue.trim()}
                    >
                        Найти
                    </Button>
                </div>
            </form>
        );
    },
);
SearchInput.displayName = 'SearchInput';

export default SearchInput;
